import React from "react";
import { useTranslation } from "react-i18next";
import { QrIcon } from "./icons/QrIcon";
import Header from "./reusable/Header";
import { EmailIcon } from "./icons/EmailIcon";
import QRCode from "react-qr-code";
import { Button, Input } from "@nextui-org/react";
import { MailIcon } from "./icons/MailIcon";
import { ArrowForward } from "./icons/ArrowForward";
import {
  sendMobileEmail,
  getVerificationStatus,
  safeRedirect,
} from "../store/verification.slice";
import Loading from "./reusable/Loading";
import VerificationComplete from "./reusable/VerificationComplete";
import PoweredBy from "./reusable/PoweredBy";
import { trackEvent } from "../utils/analytics";

const methods = [
  {
    id: "QR",
    icon: <QrIcon height={28} width={28} />,
    key: "qr",
  },
  {
    id: "EMAIL",
    icon: <EmailIcon height={28} width={28} fill={"var(--urtentic-sdk-primary, #255CFF)"} />,
    key: "email",
  },
];

function SwitchMethods({ selectMethod }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col w-full h-full">
      <Header />
      <div className="flex flex-col p-5 h-full justify-between">

        {/* Hero */}
        <div className="flex flex-col items-center text-center gap-2">
          <div className="heading text-2xl">{t("mobilePrompt.switchTitle")}</div>
          <div className="desc text-sm leading-relaxed" style={{color: "var(--urtentic-sdk-subtitle, #737373)"}}>
            {t("mobilePrompt.switchDescription")}
          </div>
        </div>

        {/* Method cards */}
        <div className="flex flex-col gap-3">
          {methods.map((method) => (
            <div
              key={method.id}
              onClick={() => selectMethod(method.id)}
              className="flex flex-row items-center gap-4 p-4 rounded-xl background border border-transparent hover:border-blue-200 transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-center rounded-full border icon-badge-bg h-12 w-12 shrink-0">
                {method.icon}
              </div>
              <div className="flex flex-col gap-0.5 flex-grow">
                <div className="font-semibold text-sm" style={{color: "var(--urtentic-sdk-body, #737373)"}}>{t(`mobilePrompt.methods.${method.key}.title`)}</div>
                <div className="desc text-xs leading-relaxed">{t(`mobilePrompt.methods.${method.key}.desc`)}</div>
              </div>
              <ArrowForward />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-center">
          <PoweredBy />
        </div>

      </div>
    </div>
  );
}

function QRCodeView({ link, goBack }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col w-full h-full">
      <Header goBack={goBack} closeEnabled={true} />
      <div className="flex flex-col p-5 h-full">

        {/* Centred content block */}
        <div className="flex-grow flex flex-col items-center justify-center gap-5">
          <div className="flex flex-col items-center text-center gap-2">
            <div className="heading text-2xl">{t("mobilePrompt.qrView.title")}</div>
            <div className="desc text-sm leading-relaxed" style={{color: "var(--urtentic-sdk-subtitle, #737373)"}}>{t("mobilePrompt.qrView.desc")}</div>
          </div>
          <div className="p-4 rounded-2xl bg-white shadow-md border border-gray-100">
            <QRCode value={link} size={180} />
          </div>
          <div className="desc text-xs text-center px-6">
            {t("mobilePrompt.qrView.instructions")}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-center pb-2">
          <PoweredBy />
        </div>

      </div>
    </div>
  );
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function EmailView({ sendEmailFunc, setEmail, goBack, error, sent, isLoading }) {
  const { t } = useTranslation();
  const [localEmail, setLocalEmail] = React.useState("");
  const [touched, setTouched] = React.useState(false);

  const isValidEmail = EMAIL_REGEX.test(localEmail);
  const showValidationError = touched && !isValidEmail;

  const handleChange = (value) => {
    setLocalEmail(value);
    setEmail(value);
  };

  const handleSubmit = () => {
    setTouched(true);
    if (isValidEmail) sendEmailFunc();
  };

  return (
    <div className="flex flex-col w-full h-full">
      <Header goBack={goBack} closeEnabled={true} />
      <div className="flex flex-col p-5 h-full">

        {/* All content grouped naturally at top */}
        <div className="flex flex-col gap-5">
          <div className="flex flex-col items-center text-center gap-2">
            <div className="heading text-2xl">{t("mobilePrompt.emailView.title")}</div>
            <div className="desc text-sm leading-relaxed" style={{color: "var(--urtentic-sdk-subtitle, #737373)"}}>{t("mobilePrompt.emailView.desc")}</div>
          </div>

          <div className="flex flex-col gap-3">
            {sent && (
              <div className="flex flex-row items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                <span>✅</span>
                <span>{t("mobilePrompt.emailView.sentMessage")}</span>
              </div>
            )}
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                {error}
              </div>
            )}
            <Input
              type="email"
              variant="bordered"
              label={t("mobilePrompt.emailView.emailLabel")}
              placeholder="you@example.com"
              labelPlacement="outside"
              value={localEmail}
              onValueChange={handleChange}
              onBlur={() => setTouched(true)}
              isDisabled={sent}
              isInvalid={showValidationError}
              errorMessage={showValidationError ? t("mobilePrompt.emailView.invalidEmail") : ""}
              startContent={
                <MailIcon
                  className="text-2xl pointer-events-none flex-shrink-0"
                  style={{ color: "var(--urtentic-sdk-primary, #255CFF)" }}
                />
              }
            />
            <Button
              fullWidth
              radius="sm"
              color={sent ? "default" : "primary"}
              size="lg"
              isDisabled={sent || (touched && !isValidEmail) || isLoading}
              isLoading={isLoading}
              onClick={handleSubmit}
            >
              {sent ? t("mobilePrompt.emailView.sentButton") : t("mobilePrompt.emailView.sendButton")}
            </Button>
          </div>
        </div>

        {/* PoweredBy pinned to bottom */}
        <div className="flex-grow" />
        <div className="flex justify-center pb-2">
          <PoweredBy />
        </div>

      </div>
    </div>
  );
}

function MobileDevicePrompt({
  verification,
  redirectUrl,
  clientId,
  accessToken,
}) {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [emailSent, setEmailSent] = React.useState(false);
  const [isComplete, setIsComplete] = React.useState(false);

  React.useEffect(() => {
    trackEvent("mobile_handoff_prompted", {
      flowId: verification?.flowId,
      verificationId: verification?.verificationId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMethodSelect = (method) => {
    trackEvent("mobile_handoff_method_selected", {
      method,
      flowId: verification?.flowId,
      verificationId: verification?.verificationId,
    });
    setSelectedMethod(method);
  };

  const sendEmail = () => {
    setLoading(true);
    setError("");
    sendMobileEmail(
      email,
      `${window.location.href}&verificationId=${verification?.verificationId}`,
      verification?.verificationId,
      clientId,
      accessToken
    )
      .then(() => {
        trackEvent("mobile_handoff_email_sent", {
          flowId: verification?.flowId,
          verificationId: verification?.verificationId,
        });
        setEmailSent(true);
      })
      .catch(() => {
        trackEvent("mobile_handoff_email_failed", {
          flowId: verification?.flowId,
          verificationId: verification?.verificationId,
        });
        setError(t("mobilePrompt.emailView.genericError"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const goBack = () => {
    setSelectedMethod("");
    setEmail("");
    setEmailSent(false);
    setError("");
  };

  React.useEffect(() => {
    if (!verification?.verificationId) return;

    const interval = setInterval(() => {
      getVerificationStatus(verification?.verificationId, clientId, accessToken)
        .then((res) => {
          if (res.data !== "STARTED" && res.data !== "ABANDONED") {
            clearInterval(interval);
            trackEvent("mobile_handoff_completed", {
              flowId: verification?.flowId,
              verificationId: verification?.verificationId,
              status: res.data,
            });
            setIsComplete(true);
            if (redirectUrl) {
              setTimeout(() => {
                safeRedirect(redirectUrl, verification?.allowedRedirectUrls, verification?.flowId);
              }, 5000);
            }
          }
        })
        .catch((err) => {
          console.error("Error checking verification status", err);
        });
    }, 20000);

    return () => clearInterval(interval);
  }, [verification?.verificationId, verification?.allowedRedirectUrls, verification?.flowId, redirectUrl, clientId, accessToken]);

  return (
    <>
      {loading && selectedMethod !== "EMAIL" && <Loading label={t("mobilePrompt.sendingEmail")} />}
      {isComplete && <VerificationComplete message={t("verification.complete")} />}
      {!selectedMethod && !loading && !isComplete && (
        <SwitchMethods selectMethod={handleMethodSelect} />
      )}
      {selectedMethod === "QR" && !loading && !isComplete && (
        <QRCodeView
          goBack={goBack}
          link={`${window.location.href}&verificationId=${verification?.verificationId}`}
        />
      )}
      {selectedMethod === "EMAIL" && !isComplete && (
        <EmailView
          goBack={goBack}
          error={error}
          sent={emailSent}
          isLoading={loading}
          sendEmailFunc={sendEmail}
          setEmail={setEmail}
        />
      )}
    </>
  );
}

export default MobileDevicePrompt;
